/**
 * ============================================
 * Axios Instance with JWT Authentication
 * ============================================
 * 
 * Centralized Axios instance that automatically:
 * - Adds JWT token to all requests
 * - Handles token expiration
 * - Retries failed requests with new token
 */
import axios from 'axios';
import authService from '../services/authService';
import { getSellerToken, endSellerSession, SELLER_TOKEN_HEADER, SELLER_SESSION_REQUIRED } from '../utils/sellerSession';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_APIURL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Request Interceptor
 * Automatically adds JWT token to all outgoing requests
 */
api.interceptors.request.use(
  async (config) => {
    try {
      // Get current token (will refresh if expired)
      const token = await authService.refreshTokenIfNeeded();

      if (token) {
        // Add Authorization header with Bearer token
        config.headers['Authorization'] = `Bearer ${token}`;
        if (process.env.NODE_ENV !== 'production') {
          console.log('[API] Request with JWT token:', config.method.toUpperCase(), config.url);
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[API] No token available for request');
        }
      }

      // The logged-in seller, for plan checkout and invoices (utils/sellerSession.js)
      const sellerToken = getSellerToken();
      if (sellerToken) {
        config.headers[SELLER_TOKEN_HEADER] = sellerToken;
      }

      return config;
    } catch (error) {
      console.error('[API] Error in request interceptor:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles authentication errors and token expiration
 */
api.interceptors.response.use(
  (response) => {
    // Successful response, return as-is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // The seller session is missing or expired: a new client token can't fix
    // that, so the seller logs in again (keeping a plan being bought)
    if (error.response && error.response.status === 401 && error.response.data?.code === SELLER_SESSION_REQUIRED) {
      console.warn('[API] Seller session required, logging in again');
      endSellerSession();
      return Promise.reject(error);
    }

    // Check if error is due to authentication
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.warn('[API] Authentication error:', error.response.status, error.response.data?.message);

      // Prevent infinite retry loop
      if (originalRequest._retry) {
        console.error('[API] Token refresh failed, clearing token');
        authService.clearToken();
        return Promise.reject(error);
      }

      // Mark request as retried
      originalRequest._retry = true;

      try {
        // Try to get a new token
        console.log('[API] Attempting to refresh token...');
        const newToken = await authService.initializeToken();

        // Update Authorization header with new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        // Retry original request with new token
        console.log('[API] Retrying request with new token');
        return api(originalRequest);
      } catch (refreshError) {
        console.error('[API] Failed to refresh token:', refreshError);
        authService.clearToken();
        return Promise.reject(refreshError);
      }
    }

    // For other errors, just reject
    console.error('[API] Request failed:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

export default api;
